import { useQueryCache } from '@pinia/colada';
import { acceptHMRUpdate, defineStore } from 'pinia';

import { useAlertsStore } from '@tallo/design-system';
import { FileDto } from '@tallo/file-manager';
import { Coordinates, PropertyDto, PropertyStatus } from '@tallo/property';

import { SaveAccessibilityDto } from '../dtos/save-accessibility.dto.interface';
import { SaveAmenitiesDto } from '../dtos/save-amenities.dto.interface';
import { SaveIncludedUtilitiesDto } from '../dtos/save-included-utilities.dto.interface';
import { SaveLeaseConditionsDto } from '../dtos/save-lease-condition.dto.interface';
import { SaveLocationDto } from '../dtos/save-location.dto.interface';
import { SaveParkingDto } from '../dtos/save-parking.dto.interface';
import { SavePetPolicyDto } from '../dtos/save-pet-policy.dto.interface';
import { SavePropertySpecificationsDto } from '../dtos/save-property-specifications.dto.interface';
import { SavePropertyRenterRequirementsDto } from '../dtos/save-renter-requirements.dto.interface';
import { portfolioQueryKey } from '../queries/portfolio.query';
import propertyService from '../services/property.service';

interface PropertyState {
  property: PropertyDto | undefined;
  isLoading: boolean;
  isLoaded: boolean;
}

export const usePropertyStore = defineStore({
  id: 'property',
  state: (): PropertyState => {
    return {
      property: undefined,
      isLoading: false,
      isLoaded: false,
    };
  },
  getters: {
    coordinates(state): Coordinates | null {
      const location = state.property?.location;

      if (!location?.latitude || !location?.longitude) {
        return null;
      }

      return {
        latitude: location.latitude,
        longitude: location.longitude,
      };
    },

    images(state): FileDto[] {
      return (state.property?.images || []).sort((a, b) => a!.order - b!.order);
    },
  },
  actions: {
    setProperty(property: PropertyDto) {
      this.property = property;
    },

    setImages(images: FileDto[]): void {
      this.property!.images = images;
    },

    async loadProperty(propertyId: string) {
      this.isLoading = true;
      this.isLoaded = false;

      const { data } = await propertyService.getById(propertyId);

      this.setProperty(data);
      this.isLoading = false;
      this.isLoaded = true;
    },

    async deleteProperty(propertyId: string) {
      const queryCache = useQueryCache();

      await propertyService.delete(propertyId);

      queryCache.invalidateQueries({ key: [portfolioQueryKey] });
    },

    async startPropertyListing(propertyId: string) {
      const queryCache = useQueryCache();
      const alertsStore = useAlertsStore();

      try {
        const { data } = await propertyService.startListing(propertyId);

        Object.assign(this.property!, data);

        queryCache.invalidateQueries({ key: [portfolioQueryKey] });
      } catch (error) {
        alertsStore.somethingWentWrong();
      }
    },

    async pausePropertyListing(propertyId: string) {
      const queryCache = useQueryCache();
      const alertsStore = useAlertsStore();

      try {
        const { data } = await propertyService.stopListing(propertyId);

        Object.assign(this.property!, data);
        alertsStore.success(`<b>${this.property?.displayName}</b> has been unlisted`);

        await Promise.all([queryCache.invalidateQueries({ key: [portfolioQueryKey] })]);
      } catch (error) {
        alertsStore.somethingWentWrong();
      }
    },

    async markAsRentedOut(propertyId: string) {
      const queryCache = useQueryCache();
      const { data } = await propertyService.markAsRentedOut(propertyId);

      Object.assign(this.property!, data);

      await Promise.all([queryCache.invalidateQueries({ key: [portfolioQueryKey] })]);
    },

    async updateDisplayName(displayName: string) {
      const queryCache = useQueryCache();

      const response = await propertyService.updateProperty(this.property!.id, { displayName });

      this.property!.displayName = response.data.displayName;

      queryCache.invalidateQueries({ key: [portfolioQueryKey] });
    },

    async saveDescription(description: string) {
      const { data } = await propertyService.updateProperty(this.property?.id as string, { description });

      this.property!.description = data.description;
    },

    async saveHighlights(highlights: string) {
      const { data } = await propertyService.updateProperty(this.property?.id as string, { highlights });

      this.property!.highlights = data.highlights;
    },

    async saveStatus(status: PropertyStatus) {
      const queryCache = useQueryCache();
      const { data } = await propertyService.updateProperty(this.property?.id as string, { status });

      this.property!.status = data.status;
      queryCache.invalidateQueries({ key: [portfolioQueryKey] });
    },

    async generateDescription(): Promise<string> {
      const { data } = await propertyService.generateDescription(this.property?.id as string);

      return data;
    },

    async saveRenterRequirements(payload: SavePropertyRenterRequirementsDto) {
      const renterRequirementsId = this.property?.renterRequirements?.id;
      const { data } = renterRequirementsId
        ? await propertyService.updateRenterRequirements(this.property?.id!, renterRequirementsId, payload)
        : await propertyService.createRenterRequirements(this.property?.id!, payload);

      this.property!.renterRequirements = data;
    },

    async saveSpecifications(payload: SavePropertySpecificationsDto) {
      const specificationId = this.property?.specifications?.id;
      const { data } = specificationId
        ? await propertyService.updateSpecifications(this.property?.id!, specificationId, payload)
        : await propertyService.createSpecifications(this.property?.id!, payload);

      this.property!.specifications = data;
    },

    async saveAccessibility(payload: SaveAccessibilityDto) {
      const accessibilityId = this.property?.accessibility?.id;
      const { data } = accessibilityId
        ? await propertyService.updateAccessibility(this.property?.id!, accessibilityId, payload)
        : await propertyService.createAccessibility(this.property?.id!, payload);

      this.property!.accessibility = data;
    },

    async saveAmenities(payload: SaveAmenitiesDto) {
      const amenitiesId = this.property?.amenities?.id;
      const { data } = amenitiesId
        ? await propertyService.updateAmenities(this.property?.id!, amenitiesId, payload)
        : await propertyService.createAmenities(this.property?.id!, payload);

      this.property!.amenities = data;
    },

    async saveIncludedUtilities(payload: SaveIncludedUtilitiesDto) {
      const includedUtilitiesId = this.property?.includedUtilities?.id;
      const { data } = includedUtilitiesId
        ? await propertyService.updateIncludedUtilities(this.property?.id!, includedUtilitiesId, payload)
        : await propertyService.createIncludedUtilities(this.property?.id!, payload);

      this.property!.includedUtilities = data;
    },

    async saveLeaseConditions(form: SaveLeaseConditionsDto) {
      const leaseConditionsId = this.property?.leaseConditions?.id;
      const { data } = leaseConditionsId
        ? await propertyService.updateLeaseConditions(this.property?.id!, leaseConditionsId, form)
        : await propertyService.createLeaseConditions(this.property?.id!, form);

      this.property!.leaseConditions = data;
    },

    async saveParking(payload: SaveParkingDto) {
      const parkingId = this.property?.parking?.id;
      const { data } = parkingId
        ? await propertyService.updateParking(this.property?.id!, parkingId, payload)
        : await propertyService.createParking(this.property?.id!, payload);

      this.property!.parking = data;
    },

    async savePetPolicy(payload: SavePetPolicyDto) {
      const petPolicyId = this.property?.petPolicy?.id;
      const { data } = petPolicyId
        ? await propertyService.updatePetPolicy(this.property?.id!, petPolicyId, payload)
        : await propertyService.createPetPolicy(this.property?.id!, payload);

      this.property!.petPolicy = data;
    },

    async saveLocation(payload: SaveLocationDto) {
      const queryCache = useQueryCache();
      const propertyId = this.property?.id;

      if (!propertyId) {
        const { data } = await propertyService.create(payload);

        this.setProperty(data);
      } else {
        const locationId = this.property?.location?.id! as string;
        const { data } = await propertyService.updateLocation(propertyId as string, locationId, payload);

        const oldLocation = this.property!.location;

        if (
          oldLocation.address !== data.address ||
          oldLocation.city !== data.city ||
          oldLocation.state !== data.state ||
          oldLocation.zip !== data.zip
        ) {
          this.property!.displayName = data.address;
        }

        this.property!.location = data;
      }

      queryCache.invalidateQueries({ key: [portfolioQueryKey] });
    },

    async deleteImage(imageId: string) {
      const imageIndex = this.images.findIndex(({ id }) => id === imageId);

      if (imageIndex > -1) {
        this.property?.images.splice(imageIndex, 1);
      }

      await propertyService.deleteImage(this.property!.id, imageId);
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePropertyStore, import.meta.hot));
}
